import type { IEquipmentAsset } from '@/Components/Pages/EquipmentPage/equipment.types';
import { authedFetch, ProviderErrorResponse, UnauthorizedError } from '@apis/auth';
import type { ProviderName } from '@interfaces/IAuth';
import { sseFrames } from './sseFrames';

/**
 * Mirrors monitor.ts's error model so the Equipment page can render the same
 * banner/skeleton tree as other site-scoped surfaces:
 *
 *   - 401 → UnauthorizedError (global redirect handles it)
 *   - 409 PROVIDER_NOT_CONNECTED / PROVIDER_DISCONNECTED → ProviderErrorResponse
 *   - 409 SITE_NOT_LINKED → SiteNotLinkedError
 *   - 502 → UpstreamError
 *
 * `SiteNotLinkedError` and `UpstreamError` are duplicated rather than shared
 * with monitor.ts to avoid circular `apis/*` dependencies; if a third
 * consumer needs them they can move into a shared module.
 */
export class SiteNotLinkedError extends Error {
  constructor(public readonly siteId: string) {
    super(`site ${siteId} has no trusstor project linked`);
    this.name = 'SiteNotLinkedError';
  }
}

export class UpstreamError extends Error {
  constructor() {
    super('upstream provider failed');
    this.name = 'UpstreamError';
  }
}

export async function parseStandardErrors(res: Response): Promise<never> {
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  const error = body?.error as Record<string, unknown> | undefined;
  const code = error?.code as string | undefined;
  const details = error?.details as Record<string, unknown> | undefined;

  if (res.status === 401) throw new UnauthorizedError();
  if (
    res.status === 409 &&
    (code === 'PROVIDER_NOT_CONNECTED' || code === 'PROVIDER_DISCONNECTED')
  ) {
    throw new ProviderErrorResponse(code, details?.provider as ProviderName);
  }
  if (res.status === 409 && code === 'SITE_NOT_LINKED') {
    throw new SiteNotLinkedError((details?.siteId as string | undefined) ?? '');
  }
  if (res.status === 502) throw new UpstreamError();

  throw new Error(`request failed: ${res.status} ${(error?.message as string | undefined) ?? ''}`);
}

export interface IUtilizationDay {
  date: string;
  activeMinutes: number;
  utilizationPct: number;
  hasData: boolean;
  /** This day's route failed to load (hasData is then false). */
  unavailable?: boolean;
  /** Not loaded yet: a `day` event with its figures follows. */
  pending?: boolean;
}

/**
 * The site's equipment registry, already sorted by type then name. The `/assets`
 * suffix keeps it apart from the Daily Route picker at `/api/sites/:siteId/equipment`.
 */
export async function fetchSiteEquipmentAssets(
  siteId: string,
  signal?: AbortSignal,
): Promise<IEquipmentAsset[]> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/equipment/assets`,
    { signal },
  );
  if (res.ok) return (await res.json()) as IEquipmentAsset[];
  return parseStandardErrors(res);
}

export interface IEquipmentActivity {
  /** The 6 finished days before today, oldest first. */
  utilization: IUtilizationDay[];
}

/**
 * One frame of the activity stream. `week` comes first, from the server's
 * cache: every day, the uncached ones `pending`. Each pending day then arrives
 * as a `day`.
 */
export type EquipmentActivityEvent =
  | { type: 'week'; utilization: IUtilizationDay[] }
  | { type: 'day'; day: IUtilizationDay };

export interface IEquipmentTimeline {
  /** Today's chart bar, so far. */
  today: IUtilizationDay;
  stops: import('@apis/reports').DailyRouteStop[];
}

function assetPath(siteId: string, equipmentId: string, resource: 'activity' | 'timeline'): string {
  return `/api/sites/${encodeURIComponent(siteId)}/equipment/${encodeURIComponent(equipmentId)}/${resource}`;
}

/**
 * The weekly chart's 6 finished days, as the server resolves them. An `error`
 * frame — the stream failing after it began — throws, as the same failure
 * before it began would have: a dead upstream session as `UnauthorizedError`,
 * anything else as `UpstreamError`.
 */
export async function* streamEquipmentActivity(
  siteId: string,
  equipmentId: string,
  signal?: AbortSignal,
): AsyncGenerator<EquipmentActivityEvent> {
  const res = await authedFetch(assetPath(siteId, equipmentId, 'activity'), {
    signal,
    headers: { Accept: 'text/event-stream' },
  });
  if (!res.ok || !res.body) return parseStandardErrors(res);
  for await (const frame of sseFrames(res.body)) {
    const data: unknown = JSON.parse(frame.data);
    if (frame.event === 'week' || frame.event === 'day') {
      yield data as EquipmentActivityEvent;
    } else if (frame.event === 'error') {
      if ((data as { code?: string }).code === 'UPSTREAM_UNAUTHORIZED') throw new UnauthorizedError();
      throw new UpstreamError();
    }
  }
}

export async function fetchEquipmentTimeline(
  siteId: string,
  equipmentId: string,
  signal?: AbortSignal,
): Promise<IEquipmentTimeline> {
  const res = await authedFetch(assetPath(siteId, equipmentId, 'timeline'), { signal });
  if (res.ok) return (await res.json()) as IEquipmentTimeline;
  return parseStandardErrors(res);
}
